import React from 'react'
import { useTheme } from '@emotion/react'
import type { PublicationCardItemWithVideo } from '@news-mono/web-common'
import type { VideoMetaDTO } from '@west-australian-newspapers/publication-types'

import { patchVerticalImageCrops } from './helpers/patchVerticalImageCrops'
import { calculateDuration } from '../VideoCard/helpers/calculateDuration'

import {
    StyledItem,
    StyledVerticalCardMedia,
    StyledMeta,
    StyledTitle,
    StyledDuration,
    StyledGradient,
} from './VerticalVideoCard.styled'

export interface VerticalVideoCardProps {
    cardItem: PublicationCardItemWithVideo
    videoMeta: VideoMetaDTO
    isLoading?: boolean
    onSelect: (video: VideoMetaDTO) => void
    innerRef?: React.Ref<HTMLDivElement>
}

export const VerticalVideoCard: React.FC<VerticalVideoCardProps> = ({
    cardItem,
    videoMeta,
    isLoading = false,
    onSelect,
    innerRef,
}) => {
    const patchedItem = patchVerticalImageCrops(cardItem, videoMeta)
    const image = patchedItem.image
    const original = image?.crops?.original

    // Detect if poster is horizontal (protect against stretching)
    const isHorizontal = original && original.width > original.height * 1.05

    // Use doubled width when horizontal → less pixelation after vertical crop
    const imageWidthsToUse = isHorizontal
        ? {
              mobile: '476px',
              tablet: '476px',
              desktop: '476px',
              fallbackWidth: 476,
          }
        : {
              mobile: '238px',
              tablet: '238px',
              desktop: '238px',
              fallbackWidth: 238,
          }

    const altText = image?.altText ?? cardItem.headline ?? cardItem.teaser ?? ''

    const handleClick = () => {
        if (!isLoading) onSelect(videoMeta)
    }

    return (
        <StyledItem ref={innerRef} onClick={handleClick}>
            {!isLoading && (
                <>
                    <StyledVerticalCardMedia
                        item={{
                            loaded: true,
                            result: patchedItem,
                        }}
                        onEvent={() => {}}
                        imageWidths={imageWidthsToUse}
                        fixedRatio="9:16"
                    />

                    <StyledGradient />

                    <StyledMeta>
                        <StyledTitle>{cardItem.headline}</StyledTitle>
                        <StyledDuration>
                            {calculateDuration(cardItem.video.duration)}
                        </StyledDuration>
                    </StyledMeta>
                </>
            )}

            {isLoading && <StyledTitle>{altText}</StyledTitle>}
        </StyledItem>
    )
}
